/**
 * Encryption API Service
 *
 * Handles communication with the backend E2E encryption endpoints
 */

import { publicKeyToString } from '../lib/crypto/e2e';
import { apiFetch } from './http';

const API_BASE = '/api/messaging';

export interface RegisterKeyRequest {
  userId: string;
  deviceId: string;
  publicKey: string; // hex-encoded
}

export interface RegisterKeyResponse {
  keyId: string;
  userId: string;
  deviceId: string;
  publicKey: string;
  createdAt: string;
}

export interface UserPublicKey {
  userId: string;
  deviceId: string;
  publicKey: string;
  createdAt: string;
}

export interface GetPublicKeysResponse {
  keys: UserPublicKey[];
}

export interface RotateKeyRequest {
  userId: string;
  deviceId: string;
  oldPublicKey: string;
  newPublicKey: string;
  reason?: string;
}

/**
 * Register public key with server
 * Private key is NEVER sent to server
 */
export async function registerPublicKey(
  userId: string,
  deviceId: string,
  publicKey: Uint8Array
): Promise<RegisterKeyResponse> {
  return apiFetch<RegisterKeyResponse>(`${API_BASE}/keys/register`, {
    method: 'POST',
    body: JSON.stringify({
      userId,
      deviceId,
      publicKey: publicKeyToString(publicKey),
    }),
  });
}

/**
 * Get public keys for specified users
 * Used to encrypt messages for recipients
 */
export async function getPublicKeys(userIds: string[]): Promise<GetPublicKeysResponse> {
  const params = new URLSearchParams({
    userIds: userIds.join(','),
  });

  return apiFetch<GetPublicKeysResponse>(`${API_BASE}/keys?${params}`, {
    method: 'GET',
  });
}

/**
 * Rotate encryption keys
 * Creates new keypair and invalidates old one
 */
export async function rotateKey(request: RotateKeyRequest): Promise<void> {
  await apiFetch<void>(`${API_BASE}/keys/rotate`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Generate a new keypair (debug/testing only)
 * Production apps should generate keypairs client-side
 */
export async function generateKeyPairServer(): Promise<{
  publicKey: string;
  privateKey: string;
  deviceId: string;
  warning: string;
}> {
  return apiFetch<{
    publicKey: string;
    privateKey: string;
    deviceId: string;
    warning: string;
  }>(`${API_BASE}/keys/generate`, {
    method: 'POST',
  });
}
